import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import {
  approveReportPostApi,
  fetchReportPostByStateApi,
  rejectReportPostApi,
  StateReport,
  getAdminPostDetail,
} from '../../../../api/admin/post.api';
import { useReportPostManagementHook } from '../../../../hooks/admin/report-post/use-report-post';
import { fetchUserProfileByIdApi } from '../../../../api';
import dayjs from 'dayjs';
import { adminHidePostByReportApi } from '../../../../api/posts/report-post.apt';
import toast from 'react-hot-toast';
import { AdminButton, AdminPageHeader, AdminPillTabs, AdminStatusPill } from '../../ui';
import {
  PostHead,
  PostMedia,
  PostText,
  hasPostMedia,
  useSensitiveCover,
} from '../post-moderation/AdminPostParts';

const ManagementReportPost = (props) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [isRefetch, setIsRefetch] = useState(false);
  const [status, setStatus] = useState(0);
  const { data } = useReportPostManagementHook({ isRefetch, status });
  const [lengthReportPending, setLengthReportPending] = useState(0);
  const [lengthReportAccepted, setLengthReportAccepted] = useState(0);
  const [lengthReportRejected, setLengthReportRejected] = useState(0);

  const getAllPostReportPending = async () => {
    try {
      const { data } = await fetchReportPostByStateApi(0);
      setLengthReportPending(data.length);
    } catch {
      //
    } finally {
      //
    }
  };
  const getAllReportAccepted = async () => {
    try {
      const { data } = await fetchReportPostByStateApi(1);
      setLengthReportAccepted(data.length);
    } catch {
      //
    } finally {
      //
    }
  };
  const getAllReportRejected = async () => {
    try {
      const { data } = await fetchReportPostByStateApi(2);
      setLengthReportRejected(data.length);
    } catch {
      //
    } finally {
      //
    }
  };
  useEffect(() => {
    getAllReportRejected();
    getAllReportAccepted();
    getAllPostReportPending();
  }, []);

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Quản lý bài viết bị report"
        description="Xem bài bị người dùng report, lý do report và quyết định có chấp thuận hay không."
      />
      <AdminPillTabs
        ariaLabel="Lọc trạng thái report"
        value={status}
        onChange={(k) => setStatus(k)}
        items={[
          { key: StateReport.Pending, label: 'Đang chờ xử lý', count: `${lengthReportPending} report` },
          { key: StateReport.Accepted, label: 'Được chấp thuận', count: `${lengthReportAccepted} report` },
          { key: StateReport.Rejected, label: 'Không được chấp thuận', count: `${lengthReportRejected} report` },
        ]}
      />
      <div className="adm-stack">
        {data?.map((post, index) => (
          <MyComponent key={index} post={post} status={status} />
        ))}
      </div>
    </div>
  );
};

export default ManagementReportPost;

const MyComponent = ({ post, status }: { post: any; status: any }) => {
  const [item, setItem] = useState<any>();
  const [reportByUser, setReportByUser] = useState<any>();
  const { blur, notice } = useSensitiveCover(item);
  const handlePostApprove = async (reportId, postId) => {
    try {
      await approveReportPostApi(reportId);
      // Rà soát 28/10: trước đây gọi lối không tồn tại (posts/update-report-detail) với tham số đảo chỗ, không chờ kết
      // quả, nên luôn báo đã ẩn dù bài vẫn hiện.
      await adminHidePostByReportApi(postId, reportId);
      toast.success('Bài viết đã được ẩn do vi phạm');
    } catch {
      toast.error('Chưa ẩn được bài viết, vui lòng thử lại.');
    } finally {
      //
    }
  };
  const handleRejectReportPost = async (postId) => {
    try {
      await rejectReportPostApi(postId);
    } catch {
      //
    } finally {
      //
    }
  };
  useEffect(() => {
    const handleGetDetailPost = async (postId) => {
      const res = await getAdminPostDetail(postId).catch(() => null);
      setItem(res);
    };

    const getReportByUserPost = async (postId) => {
      const res = await fetchUserProfileByIdApi(postId);
      setReportByUser(res.data);
    };

    handleGetDetailPost(post.postId);
    getReportByUserPost(post.reportByUserId);
  }, [post.postId]);

  if (!item) {
    return <div>Loading...</div>;
  }

  return (
    <article className="adm-mod-card">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]" style={{ alignItems: 'start' }}>
        <div className="adm-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <PostHead post={item} />
          {notice}
          <div className={`adm-mod-card__body ${blur}`}>
            <PostText post={item} />
          </div>
          {hasPostMedia(item) && (
            <div className={`adm-mod-card__media ${blur}`}>
              <PostMedia post={item} />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 16, lineHeight: '22px', fontWeight: 600, color: 'var(--adm-heading)' }}>
            Thông tin report
          </div>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: '128px minmax(0, 1fr)',
              gap: 12,
              alignItems: 'baseline',
              margin: 0,
              fontSize: 14,
              lineHeight: '20px',
              color: 'var(--adm-text)',
            }}
          >
            <dt style={{ fontSize: 13, color: 'var(--adm-muted)' }}>Người report</dt>
            <dd style={{ margin: 0, wordBreak: 'break-word' }}>
              {reportByUser?.fullname ? reportByUser?.fullname : 'Khong Xac Dinh'}
            </dd>
            <dt style={{ fontSize: 13, color: 'var(--adm-muted)' }}>Thời gian report</dt>
            <dd style={{ margin: 0 }}>{dayjs(post?.updatedAt).format('DD-MM-YYYY')}</dd>
            <dt style={{ fontSize: 13, color: 'var(--adm-muted)' }}>Lý do report</dt>
            <dd style={{ margin: 0 }}>
              <AdminStatusPill tone="danger">{post?.reportPostValue}</AdminStatusPill>
            </dd>
            <dt style={{ fontSize: 13, color: 'var(--adm-muted)' }}>Nội dung report</dt>
            <dd style={{ margin: 0, wordBreak: 'break-word' }}>{post?.content}</dd>
          </dl>
        </div>
      </div>
      {status === StateReport.Pending && (
        <div className="adm-mod-card__actions">
          <AdminButton size={32} variant="danger-soft" onClick={() => handleRejectReportPost(post.id)}>
            Không chấp thuận
          </AdminButton>
          <AdminButton size={32} variant="success" icon={<Check size={14} />} onClick={() => handlePostApprove(post.id, post.postId)}>
            Chấp thuận
          </AdminButton>
        </div>
      )}
    </article>
  );
};
